import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { DescriptionOutlined } from '@mui/icons-material';
import {
  CommercialDashboard,
  PROPOSAL_STATUS_LABELS,
  formatDays,
  formatEuro,
} from '../../utils/commercialDashboard';
import { ConsolePanel, EmptyNote, PanelStat, StatusPill, useConsoleTableSx } from './ConsolePanel';
import { useConsolePalette } from './consolePalette';

type ProposalsPanelProps = {
  dashboard: CommercialDashboard;
  onOpenProject: (projectId: number) => void;
};

const ProposalsPanel: React.FC<ProposalsPanelProps> = ({ dashboard, onOpenProject }) => {
  const palette = useConsolePalette();
  const tableSx = useConsoleTableSx();
  const { rows, count, total_value: totalValue, avg_age_days: avgAge } = dashboard.proposals;

  return (
    <ConsolePanel
      title="Proposals in progress"
      subtitle="Proposal projects On-going or on Hold"
      icon={<DescriptionOutlined />}
      action={(
        <>
          <PanelStat label="Proposals" value={count} />
          <PanelStat label="Total value" value={formatEuro(totalValue)} />
          <PanelStat label="Avg. age" value={formatDays(avgAge)} />
        </>
      )}
      bodySx={{ p: 0, display: 'flex', flexDirection: 'column' }}
    >
      {rows.length === 0 ? (
        <Box sx={{ p: 2, flex: 1 }}>
          <EmptyNote>No proposal is open. Proposals appear here once a lead at Bidding gets its proposal project.</EmptyNote>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 284, overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={tableSx} aria-label="Proposals in progress">
            <TableHead>
              <TableRow>
                <TableCell>Client / Proposal</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell align="right">Age</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.project_id}
                  hover
                  tabIndex={0}
                  onClick={() => onOpenProject(row.project_id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') onOpenProject(row.project_id);
                  }}
                >
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.ink }}>
                      {row.client_name || 'No client'}
                    </Typography>
                    <Typography noWrap variant="caption" component="div" sx={{ color: palette.inkFaint }}>
                      {[row.nd_number, row.name].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: `${palette.inkSoft} !important`, whiteSpace: 'nowrap' }}>
                    {row.project_type || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatEuro(row.value)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatDays(row.age_days)}</TableCell>
                  <TableCell>
                    <StatusPill
                      label={PROPOSAL_STATUS_LABELS[row.status]}
                      tone={row.status === 'on_hold' ? 'slate' : 'sand'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </ConsolePanel>
  );
};

export default ProposalsPanel;
